import { useEffect, useRef } from 'react';

import { useBusinessEntity } from '@zenbusiness/application-commons-business-entity';
import { useMutation } from '@zenbusiness/application-commons-graphql';
import Box from '@zenbusiness/zui/Box';
import Button from '@zenbusiness/zui/Button';
import Center from '@zenbusiness/zui/Center';
import PageContainer from '@zenbusiness/zui/PageContainer';
import PageHeader from '@zenbusiness/zui/PageHeader';
import PageLoading from '@zenbusiness/zui/PageLoading';
import SharedAsset from '@zenbusiness/zui/SharedAsset';
import Stack from '@zenbusiness/zui/Stack';
import { Link } from 'react-router-dom';

import useOrderTaskContext from '#client/hooks/useOrderTaskContext';
import { REQUEST_BANKING_PRODUCT_OFFER_MUTATION } from '#client/mutations/product';
import { findLatestOrderTaskByServiceName } from '#client/utils/orderTasks';

const BankOfAmericaOffer = () => {
  const requested = useRef(false);
  const { businessEntityUuid } = useBusinessEntity();
  const { orderTasks, isLoadingOrderTask } = useOrderTaskContext();

  const [requestBankOfAmericaOffer, { loading }] = useMutation(
    REQUEST_BANKING_PRODUCT_OFFER_MUTATION
  );

  useEffect(() => {
    // Prevent duplicate offer requests by checking if an offer already exists or if a request is in progress
    if (isLoadingOrderTask || requested.current) {
      return;
    }

    const existingOffer = findLatestOrderTaskByServiceName(orderTasks, 'bank_of_america_offer');

    // If there's no existing offer and we haven't already requested one, proceed to request the offer
    if (!existingOffer?.type) {
      requested.current = true;
      requestBankOfAmericaOffer({ variables: { businessEntityUuid } });
    }
  }, [businessEntityUuid, isLoadingOrderTask, orderTasks, requestBankOfAmericaOffer]);

  if (isLoadingOrderTask || loading) {
    return <PageLoading />;
  }

  return (
    <PageContainer>
      <Center>
        <Stack
          gap={4}
          sx={{ maxWidth: '40.5rem' }}
        >
          <Box
            display="flex"
            justifyContent="center"
          >
            <SharedAsset
              alt=""
              src="/s2/vector/bank-of-america-with-logo.svg"
              width={288}
            />
          </Box>
          <PageHeader
            slotProps={{
              primary: {
                sx: { textAlign: 'center' }
              }
            }}
            subtitle="Thank you for your interest in opening a business bank account with Bank of America. A dedicated Bank of America team member will be in touch to help you with your business banking and financial needs."
            title={
              <>
                Thank you. <br /> You will be contacted soon.
              </>
            }
          />
          <Box
            display="flex"
            justifyContent="center"
          >
            <Button
              component={Link}
              to={`/dashboard/${businessEntityUuid}`}
              variant="outlined"
            >
              Go to dashboard
            </Button>
          </Box>
        </Stack>
      </Center>
    </PageContainer>
  );
};

export default BankOfAmericaOffer;
